import { _ } from "../../../core/polyglot/polyglot.js";
import { makeMonitorRepository } from "../../../core/repositories/repositoryBuilders.js";
import ChartOfAccounts from "../../../models/chartOfAccounts/ChartOfAccounts.js";
import getKeyFigureService from "../../../core/services/getKeyFigureService.js";
import InternationalizationService from "../../../core/services/InternationalizationService.js";
import KeyFigureContextRepositoryWrapper from "../../../core/repositories/KeyFigureContextRepositoryWrapper.js";
import KeyFigurePresetService from "../../../core/services/KeyFigurePresetService.js";
import optionsBag from "../../../models/options/optionsBag.js";
import PeriodReportAttachmentsService from "./PeriodReportAttachmentsService.js";
import PeriodReportDraft from "../../../models/accounting/periodReport/PeriodReportDraft.js";
import ReportService from "../../reports2/ReportService.js";
import ReportTemplate from "../../../models/reports/templates/ReportTemplate.js";
import session from "../../../core/session.js";
import UserReference from "../../../models/UserReference.js";

/**
 * Facilitate manipulating period reports.
 */
export default class PeriodReportService {
	constructor() {
		const antiForgeryToken = session.getAntiForgeryToken();

		this._periodReportRepository = new KeyFigureContextRepositoryWrapper({
			keyFigureService: getKeyFigureService(),
			repository: makeMonitorRepository({
				model: PeriodReportDraft,
				route: "/Accounting2/Company/#companyId/PeriodReport/?action/?id",
				defaultFilters: {
					companyId: session.getCompany().getLegacyId(),
				},
				antiForgeryToken,
			}),
		});

		this._chartOfAccountsRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/ChartOfAccounts/#id",
			model: ChartOfAccounts,
			defaultFilters: { companyId: session.getCompanyId() },
			antiForgeryToken,
		});

		this._keyFigureService = getKeyFigureService();

		this._keyFigurePresetService = new KeyFigurePresetService({
			companyId: session.getCompanyId(),
			keyFigureService: this._keyFigureService,
		});

		this._periodReportAttachmentsService =
			new PeriodReportAttachmentsService();

		this._agedInvoiceReportsFeature = optionsBag.fromId(
			"aged-invoice-reports",
		);
	}

	copyReport(periodReport) {
		const draft = PeriodReportDraft.newFromExisting({
			...this._defaultDraftSpec(),
			periodReport,
		});

		return this._saveDraft(draft);
	}

	async copyReportFromId(periodReportId) {
		const periodReport = await this._periodReportRepository.get({
			filter: { id: periodReportId },
		});

		return this.copyReport(periodReport);
	}

	async createDraft() {
		const myKeyFiguresPreset =
			await this._keyFigurePresetService.get("MyKeyFigures");

		const keyFigures = myKeyFiguresPreset.getKeyFigures();

		const draft = new PeriodReportDraft({
			...this._defaultDraftSpec(),
			keyFigures,
		});

		return this._saveDraft(draft);
	}

	async updateDraftDefaultAttachments(draft) {
		const [chartOfAccounts, keyFigures] = await Promise.all([
			this._getChartOfAccounts(),
			this._keyFigureService.getAllFromCache(),
		]);

		return this._periodReportAttachmentsService.updateDraftDefaultAttachments(
			{ draft, chartOfAccounts, keyFigures },
		);
	}

	async updateDraftSectionsAndDefaultAttachments(draft) {
		const [chartOfAccounts, keyFigures] = await Promise.all([
			this._getChartOfAccounts(),
			this._keyFigureService.getAllFromCache(),
		]);

		await this._updateDraftSections({ draft, chartOfAccounts, keyFigures });

		return this._periodReportAttachmentsService.updateDraftDefaultAttachments(
			{ draft, chartOfAccounts, keyFigures },
		);
	}

	async addReportTemplatesAttachments({ draft, reportTemplateIds }) {
		const [chartOfAccounts, keyFigures] = await Promise.all([
			this._getChartOfAccounts(),
			this._keyFigureService.getAllFromCache(),
		]);

		await this._updateDraftSections({ draft, chartOfAccounts, keyFigures });

		return this._periodReportAttachmentsService.addReportTemplatesAttachments(
			{ draft, chartOfAccounts, keyFigures, reportTemplateIds },
		);
	}

	async _getChartOfAccounts() {
		const chartOfAccounts = await this._chartOfAccountsRepository.get({
			filter: { id: session.getCurrentChartOfAccountsId() },
		});

		await this._ensureChartOfAccountsLanguage(chartOfAccounts);
		return chartOfAccounts;
	}

	_ensureChartOfAccountsLanguage(chartOfAccounts) {
		const language = chartOfAccounts && chartOfAccounts.getLanguage();

		if (!language) return Promise.resolve();

		const i18nService = InternationalizationService.instance();
		return i18nService.ensureTranslations(language);
	}

	_updateDraftSections({ draft, chartOfAccounts, keyFigures }) {
		const repository = makeMonitorRepository({
			route: "/Shared/Country/#ownerId/?reportTemplateId",
			defaultFilters: { ownerId: session.getCountry().getId() },
			model: ReportTemplate,
			antiForgeryToken: session.getAntiForgeryToken(),
		});

		const sectionUpdates = draft
			.getSections()
			.filter((section) => section.canHaveReport())
			.map(async (section) => {
				const reportTemplate = await repository.get({
					filter: {
						reportTemplateId: section.constructor.getReportId(),
					},
				});

				const reportService = new ReportService({
					reportTemplate,
					period: draft.getPeriod(),
					chartOfAccounts,
					keyFigures,
					company: session.getCompany(),
				});

				await reportService.updateReportAndData();
				section.setReport(reportService.getReport());
			});

		return Promise.all(sectionUpdates);
	}

	_defaultDraftSpec({ keyFigures } = {}) {
		return {
			author: new UserReference({
				name: session.getCurrentUser().getName(),
			}),
			period: session.getPeriod(),
			company: session.getCompany(),
			title: _("Report for {period}", {
				period: session.getPeriod().toSmartString(),
			}),
			subtitle: session.getCompany().getName(),
			keyFigures,
			wantsAgedInvoices: this._agedInvoiceReportsFeature.isActive(),
		};
	}

	async _saveDraft(draft) {
		await this.updateDraftSectionsAndDefaultAttachments(draft);

		return this._periodReportRepository.save({
			filter: { action: "Draft" },
			model: draft,
		});
	}
}
